import * as THREE from 'three';

/**
 * CharmedShader - A dreamy, love-struck visual effect
 * Features: Pink color overlay, heart particles, wavy distortion,
 * soft bloom, and romantic vignette
 */
export const CharmedShader = {
    name: 'CharmedShader',

    uniforms: {
        tDiffuse: { value: null },
        resolution: { value: new THREE.Vector2(1920, 1080) },

        // Time for animations
        time: { value: 0.0 },

        // === DREAMY WAVE DISTORTION ===
        waveIntensity: { value: 0.008 },      // How much the image waves
        waveFrequency: { value: 3.0 },        // Wave pattern frequency
        waveSpeed: { value: 1.5 },            // How fast waves animate

        // === PINK COLOR OVERLAY ===
        pinkOverlayIntensity: { value: 0.35 }, // Pink tint strength (0-1)
        pinkColorCenter: { value: new THREE.Vector3(1.0, 0.7, 0.85) },  // Soft pink at center
        pinkColorEdge: { value: new THREE.Vector3(1.0, 0.4, 0.6) },     // Hot pink at edges

        // === SOFT BLOOM / GLOW ===
        bloomIntensity: { value: 0.15 },      // Dreamy glow strength
        bloomRadius: { value: 0.004 },        // Blur radius for bloom

        // === HEART PARTICLES ===
        heartsEnabled: { value: 1.0 },        // 1.0 = show hearts
        heartsCount: { value: 15.0 },         // Number of floating hearts
        heartsSize: { value: 0.04 },          // Size of hearts
        heartsSpeed: { value: 0.5 },          // Rising speed

        // === SPARKLES ===
        sparklesEnabled: { value: 1.0 },      // 1.0 = show sparkles
        sparklesIntensity: { value: 0.6 },    // Brightness of sparkles
        sparklesCount: { value: 50.0 },       // Density of sparkles

        // === CHROMATIC LOVE SHIFT ===
        chromaShift: { value: 0.003 },        // Subtle color separation

        // === ROMANTIC VIGNETTE ===
        vignetteIntensity: { value: 0.4 },    // Edge darkening
        vignetteColor: { value: new THREE.Vector3(1.0, 0.5, 0.7) }, // Pink vignette

        // === SOFT FOCUS ===
        softFocusIntensity: { value: 0.2 },   // Overall dreamy blur

        // Gamma correction
        gamma: { value: 0.5 },
    },

    vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,

    fragmentShader: /* glsl */ `
        uniform sampler2D tDiffuse;
        uniform vec2 resolution;
        uniform float time;

        // Distortion
        uniform float waveIntensity;
        uniform float waveFrequency;
        uniform float waveSpeed;

        // Pink overlay
        uniform float pinkOverlayIntensity;
        uniform vec3 pinkColorCenter;
        uniform vec3 pinkColorEdge;

        // Bloom
        uniform float bloomIntensity;
        uniform float bloomRadius;

        // Hearts
        uniform float heartsEnabled;
        uniform float heartsCount;
        uniform float heartsSize;
        uniform float heartsSpeed;

        // Sparkles
        uniform float sparklesEnabled;
        uniform float sparklesIntensity;
        uniform float sparklesCount;

        // Chromatic
        uniform float chromaShift;

        // Vignette
        uniform float vignetteIntensity;
        uniform vec3 vignetteColor;

        // Soft focus
        uniform float softFocusIntensity;

        // Gamma
        uniform float gamma;

        varying vec2 vUv;

        // ----------------------------------------
        // Random and noise functions
        // ----------------------------------------
        float rand(vec2 co) {
            return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
        }

        float rand2(vec2 co) {
            return fract(sin(dot(co.xy, vec2(37.719, 113.157))) * 27493.8317);
        }

        // Smooth noise
        float noise(vec2 p) {
            vec2 i = floor(p);
            vec2 f = fract(p);
            f = f * f * (3.0 - 2.0 * f);
            
            float a = rand(i);
            float b = rand(i + vec2(1.0, 0.0));
            float c = rand(i + vec2(0.0, 1.0));
            float d = rand(i + vec2(1.0, 1.0));
            
            return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
        }
      
        // ----------------------------------------
        // Sparkle effect
        // ----------------------------------------
        float sparkles(vec2 uv, float time) {
            float sparkle = 0.0;
            
            for (float i = 0.0; i < 50.0; i++) {
                if (i >= sparklesCount) break;
                
                float seed = i * 13.37;
                vec2 pos = vec2(rand(vec2(seed, 0.0)), rand(vec2(seed, 1.0)));
                float twinkleSpeed = 2.0 + rand(vec2(seed, 2.0)) * 3.0;
                float phase = rand(vec2(seed, 3.0)) * 6.28;
                float size = 0.003 + rand(vec2(seed, 4.0)) * 0.004;
                
                // Twinkle animation
                float twinkle = pow(sin(time * twinkleSpeed + phase) * 0.5 + 0.5, 3.0);
                
                // Distance to sparkle center
                float d = length(uv - pos);
                float s = (1.0 - smoothstep(0.0, size, d)) * twinkle;
                
                // Add cross/star shape
                vec2 delta = abs(uv - pos);
                float cross = max(
                    (1.0 - smoothstep(0.0, size * 0.3, delta.x)) * (1.0 - smoothstep(0.0, size * 2.0, delta.y)),
                    (1.0 - smoothstep(0.0, size * 2.0, delta.x)) * (1.0 - smoothstep(0.0, size * 0.3, delta.y))
                ) * twinkle * 0.7;
                
                sparkle += (s + cross) * sparklesIntensity;
            }
            
            return sparkle;
        }

        void main() {
            vec2 uv = vUv;
            vec2 center = vec2(0.5, 0.5);
            float distFromCenter = length(uv - center);

            // ----------------------------------------
            // 1. Dreamy wave distortion
            // ----------------------------------------
            float waveX = sin(uv.y * waveFrequency * 10.0 + time * waveSpeed) * waveIntensity;
            float waveY = sin(uv.x * waveFrequency * 10.0 + time * waveSpeed * 0.7) * waveIntensity * 0.5;
            
            // Reduce distortion at center for better visibility
            float distortionMask = smoothstep(0.0, 0.5, distFromCenter);
            uv += vec2(waveX, waveY) * distortionMask;

            // ----------------------------------------
            // 2. Chromatic aberration (love shift)
            // ----------------------------------------
            float c = chromaShift * (0.8 + 0.2 * sin(time * 0.5));
            vec3 col;
            col.r = texture2D(tDiffuse, uv + vec2(c, 0.0)).r;
            col.g = texture2D(tDiffuse, uv).g;
            col.b = texture2D(tDiffuse, uv - vec2(c, 0.0)).b;

            // ----------------------------------------
            // 3. Soft focus / dreamy blur
            // ----------------------------------------
            if (softFocusIntensity > 0.01) {
                vec3 blur = vec3(0.0);
                float blurSize = bloomRadius * 2.0;
                
                // Simple box blur
                blur += texture2D(tDiffuse, uv + vec2(-blurSize, -blurSize)).rgb;
                blur += texture2D(tDiffuse, uv + vec2( blurSize, -blurSize)).rgb;
                blur += texture2D(tDiffuse, uv + vec2(-blurSize,  blurSize)).rgb;
                blur += texture2D(tDiffuse, uv + vec2( blurSize,  blurSize)).rgb;
                blur += texture2D(tDiffuse, uv + vec2(-blurSize,  0.0)).rgb;
                blur += texture2D(tDiffuse, uv + vec2( blurSize,  0.0)).rgb;
                blur += texture2D(tDiffuse, uv + vec2( 0.0, -blurSize)).rgb;
                blur += texture2D(tDiffuse, uv + vec2( 0.0,  blurSize)).rgb;
                blur /= 8.0;
                
                col = mix(col, blur, softFocusIntensity);
            }

            // ----------------------------------------
            // 4. Bloom / glow effect (brighten highlights)
            // ----------------------------------------
            if (bloomIntensity > 0.01) {
                vec3 bloom = vec3(0.0);
                float sampleCount = 0.0;
                
                for (float x = -2.0; x <= 2.0; x += 1.0) {
                    for (float y = -2.0; y <= 2.0; y += 1.0) {
                        vec2 offset = vec2(x, y) * bloomRadius;
                        vec3 texSample = texture2D(tDiffuse, uv + offset).rgb;
                        // Only bloom bright areas
                        float brightness = dot(texSample, vec3(0.299, 0.587, 0.114));
                        bloom += texSample * smoothstep(0.4, 0.8, brightness);
                        sampleCount += 1.0;
                    }
                }
                bloom /= sampleCount;
                
                // Add bloom with pink tint
                vec3 pinkBloom = bloom * vec3(1.2, 0.9, 1.1);
                col += pinkBloom * bloomIntensity;
            }

            // ----------------------------------------
            // 5. Pink color overlay (gradient from center to edge)
            // ----------------------------------------
            vec3 pinkGradient = mix(pinkColorCenter, pinkColorEdge, clamp(distFromCenter * 1.5, 0.0, 1.0));
            
            // Screen blend for pink overlay (preserves brightness better)
            vec3 pinkBlend = 1.0 - (1.0 - col) * (1.0 - pinkGradient * pinkOverlayIntensity * 0.5);
            
            // Mix with original based on intensity
            col = mix(col, pinkBlend, pinkOverlayIntensity);

            // ----------------------------------------
            // 6. Saturate colors slightly for dreamy look
            // ----------------------------------------
            float luminance = dot(col, vec3(0.299, 0.587, 0.114));
            col = mix(vec3(luminance), col, 1.15); // Boost saturation by 15%

            // ----------------------------------------
            // 8. Sparkles overlay
            // ----------------------------------------
            if (sparklesEnabled > 0.5) {
                float spark = sparkles(vUv, time);
                // Pink-white sparkles
                vec3 sparkColor = vec3(1.0, 0.9, 0.95);
                col += sparkColor * spark;
            }

            // ----------------------------------------
            // 9. Romantic vignette (pink-tinted edges)
            // ----------------------------------------
            // Softer vignette that only affects far edges
            float vignetteMask = smoothstep(0.7, 1.2, distFromCenter * 1.4);
            vec3 tintedEdge = mix(col, col * vignetteColor, vignetteMask * 0.5);
            col = mix(col, tintedEdge, vignetteIntensity);
            // Slight darkening at very edges
            col *= 1.0 - vignetteMask * vignetteIntensity * 0.3;

            // ----------------------------------------
            // 10. Pulsing brightness (heartbeat effect)
            // ----------------------------------------
            float heartbeat = sin(time * 2.0) * 0.03 + 1.0;
            col *= heartbeat;

            // ----------------------------------------
            // 11. Gamma correction
            // ----------------------------------------
            col = pow(col, vec3(gamma));

            // Clamp final color
            col = clamp(col, 0.0, 1.0);

            gl_FragColor = vec4(col, 1.0);
        }
    `,
};

/**
 * Creates default uniforms for the Charmed shader
 */
export function createCharmedUniforms(
    resolution = new THREE.Vector2(1920, 1080)
) {
    return {
        tDiffuse: { value: null },
        resolution: { value: resolution.clone() },
        time: { value: 0 },

        // Distortion
        waveIntensity: { value: 0.006 },
        waveFrequency: { value: 2.5 },
        waveSpeed: { value: 1.2 },

        // Pink overlay
        pinkOverlayIntensity: { value: 0.3 },
        pinkColorCenter: { value: new THREE.Vector3(1.0, 0.75, 0.88) },
        pinkColorEdge: { value: new THREE.Vector3(1.0, 0.45, 0.65) },

        // Bloom
        bloomIntensity: { value: 0.12 },
        bloomRadius: { value: 0.003 },

        // Hearts
        heartsEnabled: { value: 1.0 },
        heartsCount: { value: 12.0 },
        heartsSize: { value: 0.035 },
        heartsSpeed: { value: 0.4 },

        // Sparkles
        sparklesEnabled: { value: 1.0 },
        sparklesIntensity: { value: 0.5 },
        sparklesCount: { value: 40.0 },

        // Chromatic
        chromaShift: { value: 0.002 },

        // Vignette
        vignetteIntensity: { value: 0.35 },
        vignetteColor: { value: new THREE.Vector3(1.0, 0.5, 0.7) },

        // Soft focus
        softFocusIntensity: { value: 0.15 },

        // Gamma
        gamma: { value: 0.92 },
    };
}
